import React, { useState, useEffect } from "react";
import "@/App.css";
import "@/styles/design-system.css";
// build-marker: api2-migration v7.6 (2026-09-29) — forces fresh bundle hash so
// Vercel picks up the new REACT_APP_BACKEND_URL=https://api2.rebuiltindia.co.
if (typeof window !== "undefined") {
  window.__REBUILT_BUILD__ = "api2-v7.6-2026-09-29";
}
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import SplashScreen from "@/components/SplashScreen";
import LandingPage from "@/pages/LandingPage";
import CustomerLogin from "@/pages/customer/CustomerLogin";
import CustomerOnboarding from "@/pages/customer/CustomerOnboarding";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import CustomerApplication from "@/pages/customer/CustomerApplication";
import ContractMandate from "@/pages/customer/ContractMandate";
import CustomerTransactions from "@/pages/customer/CustomerTransactions";
import CustomerOffers from "@/pages/customer/CustomerOffers";
import CustomerRepayments from "@/pages/customer/CustomerRepayments";
import CustomerProfile from "@/pages/customer/CustomerProfile";
import AgentLogin from "@/pages/agent/AgentLogin";
import AgentRegister from "@/pages/agent/AgentRegister";
import AgentDashboard from "@/pages/agent/AgentDashboard";
import AgentCommissions from "@/pages/agent/AgentCommissions";
import AgentForgotPassword from "@/pages/agent/AgentForgotPassword";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import EmployeeLogin from "@/pages/employee/EmployeeLogin";
import EmployeeDashboard from "@/pages/employee/EmployeeDashboard";
import TermsOfService from "@/pages/policies/TermsOfService";
import PrivacyPolicy from "@/pages/policies/PrivacyPolicy";
import RefundPolicy from "@/pages/policies/RefundPolicy";
import PaymentGatewayPolicy from "@/pages/policies/PaymentGatewayPolicy";
import GrievancePolicy from "@/pages/policies/GrievancePolicy";
import DeleteAccount from "@/pages/policies/DeleteAccount";
import DeleteData from "@/pages/policies/DeleteData";
import DownloadApp from "@/pages/DownloadApp";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, role, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1565C0 100%)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="spinner" />
          <p className="text-white/70 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (allowedRoles && !allowedRoles.includes(role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  // Check if the user is on the admin subdomain — route them exclusively to admin flows.
  // This keeps admin traffic logically isolated from the main site.
  const isAdminHost = typeof window !== 'undefined' &&
    window.location.hostname === 'admin.rebuiltindia.co';

  if (isAdminHost) {
    return (
      <Routes>
        {/* Root and any unknown path on admin subdomain → Admin Login */}
        <Route path="/" element={<AdminLogin />} />
        <Route path="/admin-portal" element={<AdminLogin />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/*" element={<ProtectedRoute allowedRoles={["super_admin", "ops_staff"]}><AdminDashboard /></ProtectedRoute>} />
        {/* Employee login shares the admin subdomain for internal staff */}
        <Route path="/employee-login" element={<EmployeeLogin />} />
        <Route path="/employee/*" element={<ProtectedRoute allowedRoles={["employee"]}><EmployeeDashboard /></ProtectedRoute>} />
        {/* Any other path on admin subdomain falls back to admin login */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/customer/login" element={<CustomerLogin />} />
      <Route path="/customer/onboarding" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerOnboarding /></ProtectedRoute>} />
      <Route path="/customer/dashboard" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerDashboard /></ProtectedRoute>} />
      <Route path="/customer/application" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerApplication /></ProtectedRoute>} />
      <Route path="/customer/contract/:applicationId" element={<ProtectedRoute allowedRoles={["customer"]}><ContractMandate /></ProtectedRoute>} />
      <Route path="/customer/transactions" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerTransactions /></ProtectedRoute>} />
      <Route path="/customer/offers" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerOffers /></ProtectedRoute>} />
      <Route path="/customer/repayments" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerRepayments /></ProtectedRoute>} />
      <Route path="/customer/profile" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerProfile /></ProtectedRoute>} />
      <Route path="/agent/login" element={<AgentLogin />} />
      <Route path="/agent/forgot-password" element={<AgentForgotPassword />} />
      <Route path="/agent-register" element={<AgentRegister />} />
      <Route path="/agent/dashboard" element={<ProtectedRoute allowedRoles={["agent"]}><AgentDashboard /></ProtectedRoute>} />
      <Route path="/agent/commissions" element={<ProtectedRoute allowedRoles={["agent"]}><AgentCommissions /></ProtectedRoute>} />
      {/* Admin Portal — primary entry point is https://admin.rebuiltindia.co (handled above).
          /admin-portal is kept as a backup direct-access route on the main domain. */}
      <Route path="/admin-portal" element={<AdminLogin />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/*" element={<ProtectedRoute allowedRoles={["super_admin", "ops_staff"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/employee-login" element={<EmployeeLogin />} />
      <Route path="/employee/*" element={<ProtectedRoute allowedRoles={["employee"]}><EmployeeDashboard /></ProtectedRoute>} />
      <Route path="/policies/terms-of-service" element={<TermsOfService />} />
      <Route path="/policies/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/policies/refund-policy" element={<RefundPolicy />} />
      <Route path="/policies/payment-gateway-policy" element={<PaymentGatewayPolicy />} />
      <Route path="/policies/grievance-policy" element={<GrievancePolicy />} />
      {/* Google Play compliance — exact top-level paths required by Play Console */}
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/delete-data" element={<DeleteData />} />
      {/* Direct APK download for sideload installation */}
      <Route path="/download" element={<DownloadApp />} />
      <Route path="/download-app" element={<DownloadApp />} />
    </Routes>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    // Check if splash was shown recently (within 1 hour)
    const lastSplash = localStorage.getItem('rebuilt_splash_shown');
    const now = Date.now();
    
    if (lastSplash && (now - parseInt(lastSplash)) < 3600000) {
      setShowSplash(false);
    }
  }, []);

  const handleSplashComplete = () => {
    localStorage.setItem('rebuilt_splash_shown', Date.now().toString());
    setShowSplash(false);
  };

  return (
    <AuthProvider>
      <BrowserRouter>
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        <AppRoutes />
        <Toaster 
          position="top-center" 
          richColors 
          toastOptions={{
            style: {
              borderRadius: '12px',
              fontFamily: 'Inter, sans-serif',
            }
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
